import { Connection } from "src/api/connectApi/types";
import {
  ProviderSdkBundleResponse,
  ProviderSdkConfigResponse,
} from "src/clients/sdk";
import { getConnectionType } from "src/connections/connectionStatus";
import { FEATURE_FLAGS, isFeatureFlagEnabled } from "src/router/featureFlags";

export const SDK_SCRIPT_TYPE_LABEL = "SDK Script";
export const SDK_BUNDLE_TYPE_LABEL = "SDK Bundle";

export type ConnectionListRow =
  | {
      kind: "connection";
      id: string;
      connection: Connection;
    }
  | {
      kind: "sdkScript";
      id: string;
      script: ProviderSdkConfigResponse;
      /** Names of bundles this script already belongs to (for the merge tooltip). */
      bundleNames: string[];
    }
  | {
      kind: "sdkBundle";
      id: string;
      bundle: ProviderSdkBundleResponse;
    };

/**
 * Combines connections, SDK scripts and SDK bundles into a single flat list of
 * rows for the connections table. SDK scripts and bundles each render as their
 * own top-level row; a bundle row summarises its member scripts via pills.
 *
 * The inbound webhook an Elements connection auto-creates is an implementation
 * detail, so it gets no row at all; the show-elements-webhooks flag reveals it
 * as an ordinary connection row.
 */
export const buildConnectionListRows = ({
  connections,
  scripts,
  bundles,
}: {
  connections: Connection[];
  scripts: ProviderSdkConfigResponse[];
  bundles: ProviderSdkBundleResponse[];
}): ConnectionListRow[] => {
  const bundleNamesByScriptId = bundles.reduce<Record<string, string[]>>(
    (acc, bundle) => {
      bundle.configs.forEach((config) => {
        acc[config.id] = [...(acc[config.id] ?? []), bundle.name];
      });
      return acc;
    },
    {},
  );

  const hiddenWebhookIds = isFeatureFlagEnabled(
    FEATURE_FLAGS.showElementsWebhooks,
  )
    ? new Set<string>()
    : new Set(
        connections
          .map((connection) => connection.metadata?.webhook_connection_id)
          .filter((id): id is string => Boolean(id)),
      );

  const connectionRows = connections
    .filter((connection) => !hiddenWebhookIds.has(connection.id))
    .map<ConnectionListRow>((connection) => ({
      kind: "connection",
      id: connection.id,
      connection,
    }));

  const scriptRows = scripts.map<ConnectionListRow>((script) => ({
    kind: "sdkScript",
    id: script.id,
    script,
    bundleNames: bundleNamesByScriptId[script.id] ?? [],
  }));

  const bundleRows = bundles.map<ConnectionListRow>((bundle) => ({
    kind: "sdkBundle",
    id: bundle.id,
    bundle,
  }));

  return [...connectionRows, ...scriptRows, ...bundleRows];
};

export const getRowIdValue = (row: ConnectionListRow): string => {
  switch (row.kind) {
    case "connection":
      return row.connection.id;
    case "sdkScript":
      return row.script.id;
    case "sdkBundle":
      return row.bundle.id;
  }
};

export const getRowNameValue = (row: ConnectionListRow): string => {
  switch (row.kind) {
    case "connection":
      return row.connection.name;
    case "sdkScript":
      return row.script.name;
    case "sdkBundle":
      return row.bundle.name;
  }
};

export const getRowTypeValue = (row: ConnectionListRow): string => {
  switch (row.kind) {
    case "connection":
      return getConnectionType(row.connection);
    case "sdkScript":
      return SDK_SCRIPT_TYPE_LABEL;
    case "sdkBundle":
      return SDK_BUNDLE_TYPE_LABEL;
  }
};

/** Raw ISO timestamps, so the table can sort chronologically. */
export const getRowUpdatedAt = (row: ConnectionListRow): string => {
  switch (row.kind) {
    case "connection":
      return row.connection.updated_at;
    case "sdkScript":
      return row.script.updated_at;
    case "sdkBundle":
      return row.bundle.updated_at;
  }
};

export const getRowCreatedAt = (row: ConnectionListRow): string => {
  switch (row.kind) {
    case "connection":
      return row.connection.created_at;
    case "sdkScript":
      return row.script.created_at;
    case "sdkBundle":
      return row.bundle.created_at;
  }
};
